import { shuffle } from 'lodash'
import { isLocalhost } from './utils'
import { ThemeValue } from './theme'
import {
  getDecodedHashParam,
  getHashParams,
  HASH_PARAM_KEY_DIFFICULTY,
  HASH_PARAM_KEY_ID,
} from './hashUtils'
import PKG from '../../package.json'
import { Difficulty, State as GameState } from '../hooks/gameReducer'
import { Group, State as WordState } from '../hooks/wordReducer'
import { Word } from './statuses'

export const gameKey = 'game'
export const wordKey = 'wordList'
export const idKey = 'id'

// SSR/Node.js safe localStorage proxy
const safeStorage =
  typeof localStorage !== 'undefined'
    ? localStorage
    : {
        getItem: (_key: string) => null,
        setItem: (_key: string, _value: string) => {},
        removeItem: (_key: string) => {},
        key: (_index: number) => null,
        clear: () => {},
        length: 0,
      }

// Node.js 16+ has globalThis.atob/btoa; browser has window.btoa/atob
const _btoa: (s: string) => string =
  typeof window !== 'undefined'
    ? (s) => window.btoa(s)
    : (s) => Buffer.from(s).toString('base64')

const _atob: (s: string) => string =
  typeof window !== 'undefined'
    ? (s) => window.atob(s)
    : (s) => Buffer.from(s, 'base64').toString('binary')

const encrypt = (value: string): string => {
  const buffer = _btoa(encodeURIComponent(value)).split('')
  const reverseBuffer = buffer.reverse()

  return _btoa(
    shuffle(buffer).join('') + reverseBuffer.join('') + shuffle(buffer).join('')
  )
}

const decrypt = (value: string): string => {
  const buffer = _atob(value)
  const length = buffer.length / 3
  const reverseBuffer = buffer.substring(length, length * 2)

  return decodeURIComponent(_atob(reverseBuffer.split('').reverse().join('')))
}

export const getKey = (key: string, version?: string) => {
  return key === 'id' ? key : `${key}-${version ?? PKG.version}`
}

export const setItem = (
  key: string,
  value: string,
  version?: string,
  shouldEncrypt?: boolean
) => {
  safeStorage.setItem(
    getKey(key, version),
    isLocalhost() || !shouldEncrypt ? value : encrypt(value)
  )
}

export const getItem = (key: string, version?: string) => {
  const value = safeStorage.getItem(getKey(key, version))

  try {
    if (value) {
      JSON.parse(value)
      return value
    }
  } catch (err) {}

  return value && value !== 'NaN' ? decrypt(value) : null
}

// Find any existing versioned localStorage key and return its value
export const findAnyVersionedItem = (key: string): string | null => {
  // First try current version
  const current = getItem(key)
  if (current) return current

  // Search through all localStorage keys for any version of this key
  const keyPattern = new RegExp(`^${key}-\\d+\\.\\d+\\.\\d+$`)
  const allKeys =
    typeof localStorage !== 'undefined'
      ? Object.keys(localStorage).filter((k) => keyPattern.test(k))
      : []

  // Sort by version number descending to get the latest
  allKeys.sort((a, b) => {
    const versionA = a.replace(`${key}-`, '').split('.').map(Number)
    const versionB = b.replace(`${key}-`, '').split('.').map(Number)
    for (let i = 0; i < 3; i++) {
      if (versionA[i] !== versionB[i]) return versionB[i] - versionA[i]
    }
    return 0
  })

  // Try each version from newest to oldest
  for (const storageKey of allKeys) {
    const version = storageKey.replace(`${key}-`, '')
    const value = getItem(key, version)
    if (value) return value
  }

  return null
}

export const createId = () => {
  const hashId = getDecodedHashParam(HASH_PARAM_KEY_ID)

  // Try to find id from any existing version
  const existingData = getItem(idKey) ?? findAnyVersionedItem(gameKey)
  const current = existingData ? JSON.parse(existingData)?.id : null

  const id = hashId || current || Math.random().toString(36).substr(2, 10)

  return id
}

export const createDifficulty = (): Difficulty => {
  const difficulty = parseInt(getHashParams()[HASH_PARAM_KEY_DIFFICULTY] ?? '')

  return (
    difficulty && !isNaN(difficulty) && difficulty >= 3 && difficulty <= 9
      ? difficulty
      : 5
  ) as Difficulty
}

export const loadInitialTheme = (): ThemeValue => {
  const gameState = getItem(gameKey)
  if (gameState) {
    const savedTheme = (JSON.parse(gameState) as GameState)?.theme

    if (savedTheme) {
      return savedTheme as ThemeValue
    }
  }

  if (typeof window === 'undefined') return 'light'
  const userMedia = window.matchMedia('(prefers-color-scheme: dark)')
  if (userMedia.matches) {
    return 'dark'
  }

  return 'light' // light theme as the default
}

export const getWordsFromLocalStorage = (
  group: Group,
  difficulty: Difficulty
): Word[] | undefined => {
  const wordState = getItem(wordKey)

  if (!wordState) {
    return undefined
  }

  const words = JSON.parse(wordState) as WordState

  const selectedGroup = words?.[group]

  if (selectedGroup?.[0]?.length !== difficulty) {
    return undefined
  }

  return words?.[group] ?? words?.[group]
}

export const getSize = () =>
  typeof localStorage !== 'undefined'
    ? new Blob(Object.values(localStorage)).size
    : 0

export const truncate = (...excludes: string[]) => {
  if (typeof localStorage === 'undefined') return
  Object.keys(localStorage).forEach((itemKey) => {
    if (!excludes.includes(itemKey)) {
      localStorage.removeItem(itemKey)
    }
  })
}

// Daily game completion tracking
const COMPLETED_DAYS_KEY = 'completedDays'

export const getCompletedDays = (difficulty: Difficulty): number[] => {
  try {
    const stored = safeStorage.getItem(`${COMPLETED_DAYS_KEY}-${difficulty}`)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

export const markDayAsCompleted = (
  difficulty: Difficulty,
  dayIndex: number
): void => {
  const completed = getCompletedDays(difficulty)
  if (!completed.includes(dayIndex)) {
    completed.push(dayIndex)
    safeStorage.setItem(
      `${COMPLETED_DAYS_KEY}-${difficulty}`,
      JSON.stringify(completed)
    )
  }
}

export const isDayCompleted = (
  difficulty: Difficulty,
  dayIndex: number
): boolean => {
  return getCompletedDays(difficulty).includes(dayIndex)
}
